// ────────────────────────────────────────────────────────────────────────────
//  AppShell.tsx — Layout-Wrapper für eingeloggte Bereiche
//
//  Drei Layouts in einem (per Tailwind-Breakpoints):
//   - Desktop  (≥lg, ≥1024): Top-Bar mit Logo, Inline-Nav, Avatar-Menü.
//   - Tablet   (md..lg): Top-Bar wird kompakt, Avatar-Menü öffnet Drawer.
//   - Mobile   (<md):    Top-Bar mit Logo + Avatar, Bottom-Nav fixed unten.
//
//  Theme-aware: alle Farben kommen aus den CSS-Variablen (s. index.css).
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, NavLink } from 'react-router-dom'
import { useAuth } from '../lib/useAuth'
import { useExtensions } from '../lib/useExtensions'
import { getResolvedTheme, getThemeChoice, setThemeChoice, subscribeTheme, type ThemeChoice } from '../lib/theme'
import { useT } from '../i18n'
import { Icon, type IconName } from './Icon'

interface NavItem {
  to:     string
  labelKey: string   // i18n-key, z.B. 'nav.dashboard'
  icon:   IconName
}

const NAV_BASE: NavItem[] = [
  { to: '/app',             labelKey: 'nav.dashboard', icon: 'home' },
  { to: '/app/chat',        labelKey: 'nav.chat',      icon: 'chat' },
  { to: '/app/punch',       labelKey: 'nav.punch',     icon: 'punch' },
  { to: '/app/shifts',      labelKey: 'nav.shifts',    icon: 'calendar' },
  { to: '/app/payroll',     labelKey: 'nav.payroll',   icon: 'wallet' },
  { to: '/app/recruitment', labelKey: 'nav.recruit',   icon: 'recruit' },
  { to: '/app/org',         labelKey: 'nav.org',       icon: 'building' },
  { to: '/app/profile',     labelKey: 'nav.profile',   icon: 'user' },
]
// Mobile-Bottom-Nav: 5 Hauptpunkte — Punchclock prominent in der Mitte
// (Auf iPhone ist die Mitte der Daumen-Hotspot — perfekt für die meist-
// genutzte Aktion: Stempeln). Shop/Bug/Recruitment bleiben im Avatar-Menü.
//
// `shifts` wird in der Mobile-Nav weggelassen, weil 5 Tabs unten genug sind —
// Schichtplan ist in der Top-Bar (Desktop/Tablet) sichtbar und auf Mobile
// als Dashboard-Tile + Org-Hub-Section verfügbar.
const NAV_MOBILE_BASE: NavItem[] = [
  { to: '/app',         labelKey: 'nav.dashboard', icon: 'home'     },
  { to: '/app/chat',    labelKey: 'nav.chat',      icon: 'chat'     },
  { to: '/app/punch',   labelKey: 'nav.punch',     icon: 'punch'    },
  { to: '/app/org',     labelKey: 'nav.org',       icon: 'building' },
  { to: '/app/profile', labelKey: 'nav.profile',   icon: 'user'     },
]

/// Filtert das NAV-Array dynamisch — der Schichtplan-Eintrag wird nur gezeigt,
/// wenn der User selbst die `ShiftPlanner`-Extension hat ODER sie über die Org
/// geerbt bekommt. `useExtensions().has(...)` deckt beides ab.
function useFilteredNav(base: NavItem[]): NavItem[] {
  const { has } = useExtensions()
  return base.filter(n => n.to !== '/app/shifts' || has('ShiftPlanner'))
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  return (
    <div className="min-h-full flex flex-col bg-app-bg text-app-text">
      <TopBar />
      <main className="flex-1 pb-28 md:pb-12 animate-fade-in-up">
        {children}
      </main>
      <BottomNav />
      <div className="hidden md:block">
        <Footer />
      </div>
      {/* User-Info am unteren Rand auf Mobile (über Bottom-Nav) */}
      {user && <SafeBottomSpacer />}
    </div>
  )
}

// ── Top-Bar ─────────────────────────────────────────────────────────────────
function TopBar() {
  const { user } = useAuth()
  const t = useT()
  const NAV = useFilteredNav(NAV_BASE)
  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl border-b safe-top"
      style={{ background: 'color-mix(in srgb, var(--bg-base) 70%, transparent)', borderColor: 'var(--border)' }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-2.5 md:py-3 flex items-center gap-2 md:gap-3">
        {/* Logo */}
        <Link to="/app" className="flex items-center gap-2.5 group shrink-0">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent to-accent2 grid place-items-center font-bold text-white shadow-lg shadow-accent/30">
            C
          </span>
          <span className="font-bold text-lg tracking-tight hidden sm:inline">CustoSoft</span>
        </Link>

        {/* Inline-Nav (≥md) — Startseite + Hauptpunkte */}
        <nav className="hidden md:flex items-center gap-1 ml-4">
          <Link
            to="/"
            className="px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2 text-app-text-dim hover:text-app-text hover:bg-app-elev"
            title={t('nav.home')}
          >
            <Icon name="globe" size={18} />
            <span className="hidden lg:inline">{t('nav.home')}</span>
          </Link>
          <span className="w-px h-6 mx-1" style={{ background: 'var(--border)' }} />
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === '/app'}
              className={({ isActive }) => [
                'px-3 py-2 rounded-xl text-sm font-medium transition flex items-center gap-2',
                isActive
                  ? 'bg-accent/15 text-accent border border-accent/25'
                  : 'text-app-text-dim hover:text-app-text hover:bg-app-elev',
              ].join(' ')}
              title={t(n.labelKey)}
            >
              <Icon name={n.icon} size={18} />
              <span className="hidden lg:inline">{t(n.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        {/* Spacer + Avatar-Menü */}
        <div className="flex-1" />
        {user && <AvatarMenu />}
      </div>
    </header>
  )
}

// ── Avatar-Dropdown ─────────────────────────────────────────────────────────
function AvatarMenu() {
  const { user, logout } = useAuth()
  const nav = useNavigate()
  const t = useT()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const themeChoice = useThemeChoice()

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  if (!user) return null

  const initials = (user.displayName || user.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-app-elev transition group"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar url={user.avatarUrl} initials={initials} size={32} />
        <span className="hidden lg:block text-sm font-medium pr-1.5 max-w-[140px] truncate">
          {user.displayName || user.email}
        </span>
        <span className={`text-xs text-app-text-mute hidden lg:block transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-2xl glass-strong p-2 z-50 origin-top-right animate-fade-in-up"
          role="menu"
        >
          {/* Header — User-Info */}
          <div className="px-3 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3">
              <Avatar url={user.avatarUrl} initials={initials} size={44} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{user.displayName || '–'}</div>
                <div className="text-xs text-app-text-mute truncate">{user.email}</div>
              </div>
            </div>
            {user.appRole && (
              <div className="mt-2 text-[10px] font-bold tracking-wider uppercase text-accent">
                {user.appRole === 'SuperAdmin' ? '🛡️ SuperAdmin' : 'Staff'}
              </div>
            )}
          </div>

          {/* Theme-Switcher inline */}
          <div className="px-3 py-2.5 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[10px] font-bold tracking-wider uppercase text-app-text-mute mb-1.5">{t('profile.appearanceTitle')}</div>
            <ThemeSegmented value={themeChoice} onChange={setThemeChoice} />
          </div>

          {/* Menu items */}
          <div className="py-1">
            <MenuItem to="/app/profile" icon="user"  onClose={() => setOpen(false)}>{t('profile.title')}</MenuItem>
            <MenuItem to="/app/shop"    icon="cart"  onClose={() => setOpen(false)}>{t('nav.shop')}</MenuItem>
            <MenuItem to="/app/bug"     icon="bug"   onClose={() => setOpen(false)}>{t('nav.bug')}</MenuItem>
            <MenuItem to="/"            icon="globe" onClose={() => setOpen(false)}>{t('nav.home')}</MenuItem>
            {user.appRole && (
              <MenuItem
                external
                href="https://custosoft-api.davidschroedinger.workers.dev/admin"
                icon="shield"
                onClose={() => setOpen(false)}
              >
                {t('nav.admin')}
              </MenuItem>
            )}
          </div>

          <div className="pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => { logout(); nav('/', { replace: true }) }}
              className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-coral/10 hover:text-coral transition text-sm font-medium flex items-center gap-3"
              role="menuitem"
            >
              <Icon name="logout" size={18} className="shrink-0" /> {t('auth.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function MenuItem(props: {
  to?: string
  href?: string
  external?: boolean
  icon: IconName
  children: React.ReactNode
  onClose: () => void
}) {
  const cls = "w-full text-left px-3 py-2.5 rounded-xl hover:bg-app-elev transition text-sm font-medium flex items-center gap-3"
  if (props.external) {
    return (
      <a href={props.href} target="_blank" rel="noreferrer" onClick={props.onClose} className={cls} role="menuitem">
        <Icon name={props.icon} size={18} className="shrink-0 text-app-text-dim" /> {props.children}
      </a>
    )
  }
  return (
    <Link to={props.to!} onClick={props.onClose} className={cls} role="menuitem">
      <Icon name={props.icon} size={18} className="shrink-0 text-app-text-dim" /> {props.children}
    </Link>
  )
}

// ── Bottom-Nav (nur Mobile) ─────────────────────────────────────────────────
//
// Premium-Mobile-Nav mit:
//   • animierter "Magic-Indicator" — gleitende Pille hinter dem aktiven Item
//   • FAB-Style Center-Button (Punchclock) mit pulsierendem Glow-Halo
//   • Press-Bounce per CSS-Transform beim Tippen (haptic-like)
//   • optional Haptic-Feedback wo verfügbar (iOS Safari)
//   • Glasmorphism mit Top-Highlight und stärkerem Blur
//   • Safe-Area für iPhone-Home-Indicator korrekt respektiert
//
function BottomNav() {
  const loc = useLocation()
  const t = useT()
  const NAV_MOBILE = useFilteredNav(NAV_MOBILE_BASE)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null)

  // Active-Index ermitteln: spezielle Behandlung für /app (exact match nötig)
  const activeIdx = NAV_MOBILE.findIndex(n =>
    n.to === '/app' ? loc.pathname === '/app' : loc.pathname.startsWith(n.to)
  )

  // Magic-Indicator-Position berechnen (relativ zum Container)
  useEffect(() => {
    const recalc = () => {
      const item = itemRefs.current[activeIdx]
      const container = containerRef.current
      if (!item || !container) { setIndicatorStyle(null); return }
      const cRect = container.getBoundingClientRect()
      const iRect = item.getBoundingClientRect()
      setIndicatorStyle({
        left:  iRect.left - cRect.left + iRect.width / 2,
        width: iRect.width,
      })
    }
    recalc()
    window.addEventListener('resize', recalc)
    // Nach mount auch nochmal triggern weil layout-shift
    const id = setTimeout(recalc, 50)
    return () => { window.removeEventListener('resize', recalc); clearTimeout(id) }
  }, [activeIdx])

  // Haptic-Feedback (Vibration-API auf Android, iOS ignoriert es lautlos)
  const triggerHaptic = () => {
    try { if ('vibrate' in navigator) navigator.vibrate?.(5) } catch { /* noop */ }
  }

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 md:hidden safe-bottom"
      style={{ pointerEvents: 'none' }}
    >
      {/* Floating-Pill Container — direkt am unteren Rand, eng anliegend.
          Wichtig: KEIN overflow-hidden — sonst clippt der FAB-Button der
          oben aus dem Container herausragt. */}
      <div
        className="mx-2 mb-1 pointer-events-auto rounded-3xl relative backdrop-blur-2xl"
        style={{
          background: 'color-mix(in srgb, var(--bg-elev-2) 90%, transparent)',
          border: '1px solid var(--border-strong)',
          boxShadow: '0 12px 36px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.10)',
        }}
      >
        {/* Top-Highlight-Glow für Glas-Look */}
        <div
          aria-hidden
          className="absolute inset-x-12 top-0 h-px pointer-events-none rounded-3xl"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }}
        />

        <div ref={containerRef} className="relative max-w-lg mx-auto px-2 py-1 grid grid-cols-5">
          {/* Magic-Indicator — gleitet animiert hinter den aktiven Item-Icon.
              Top von der Icon-Mitte (~24 px ab Container-Top) ausgehend, weil
              Items jetzt vertikal zentriert sind. */}
          {indicatorStyle && activeIdx !== 2 && (
            <span
              aria-hidden
              className="absolute h-9 w-9 rounded-2xl pointer-events-none transition-all duration-[450ms]"
              style={{
                left: `${indicatorStyle.left}px`,
                top: '50%',
                transform: 'translate(-50%, -85%)',  // leicht über vertikaler Mitte → trifft Icon
                background: 'radial-gradient(circle, rgba(138,68,238,0.22), rgba(138,68,238,0.05) 70%)',
                transitionTimingFunction: 'cubic-bezier(0.34, 1.36, 0.64, 1)',
              }}
            />
          )}

          {NAV_MOBILE.map((n, idx) => {
            const active = idx === activeIdx
            const isCenter = idx === 2  // Punch — FAB-Style

            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/app'}
                ref={(el) => { itemRefs.current[idx] = el as HTMLAnchorElement | null }}
                onClick={triggerHaptic}
                className={`group flex flex-col items-center justify-center gap-0.5 py-2 rounded-2xl relative
                            transition active:scale-90`}
                style={{ transitionDuration: '220ms', transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}
              >
                {isCenter ? (
                  // ── FAB Punch — vertikal zentriert in der Pille,
                  //    KEIN Label-Text und ragt NICHT mehr oben/unten raus
                  <span className="relative">
                    {/* Pulsierender Glow-Halo */}
                    <span
                      aria-hidden
                      className={`absolute inset-0 rounded-full -z-10 transition-opacity ${
                        active ? 'animate-fab-pulse-active opacity-80' : 'animate-fab-pulse opacity-60'
                      }`}
                      style={{
                        background: 'radial-gradient(circle, rgba(138,68,238,0.55), transparent 70%)',
                        filter: 'blur(6px)',
                      }}
                    />
                    {/* Eigentlicher Button */}
                    <span
                      className={`grid place-items-center w-14 h-14 rounded-full transition-transform duration-300
                                  group-active:scale-90 ${active ? 'scale-105' : 'scale-100'}`}
                      style={{
                        background: active
                          ? 'linear-gradient(135deg, #8a44ee, #3a62ff, #33d670)'
                          : 'linear-gradient(135deg, #8a44ee, #3a62ff)',
                        boxShadow: active
                          ? '0 10px 28px rgba(138,68,238,0.55), inset 0 1px 0 rgba(255,255,255,0.30)'
                          : '0 8px 22px rgba(138,68,238,0.40), inset 0 1px 0 rgba(255,255,255,0.20)',
                        backgroundSize: active ? '200% 200%' : '100% 100%',
                        animation: active ? 'fab-gradient-shift 6s ease infinite' : undefined,
                      }}
                    >
                      <Icon name={n.icon} size={26} className="text-white drop-shadow-sm" />
                    </span>
                  </span>
                ) : (
                  // ── Normaler Tab — Icon + Label ───────────────────────
                  <>
                    <span className="relative grid place-items-center w-9 h-9">
                      <Icon
                        name={n.icon}
                        size={22}
                        className={`transition-all duration-300 ${
                          active
                            ? 'text-accent scale-110'
                            : 'text-app-text-mute group-active:scale-90 group-active:text-app-text'
                        }`}
                      />
                      {/* Chat-Item Unread-Indicator (statisch, nur wenn aktiv) */}
                      {n.to === '/app/chat' && active && (
                        <span className="absolute top-0 right-1 w-1.5 h-1.5 rounded-full bg-accent shadow"
                              style={{ boxShadow: '0 0 6px rgba(138,68,238,0.80)' }} />
                      )}
                    </span>
                    <span
                      className={`text-[10px] font-bold tracking-wide transition-colors duration-300 ${
                        active ? 'text-accent' : 'text-app-text-mute group-active:text-app-text'
                      }`}
                    >
                      {t(n.labelKey).toUpperCase()}
                    </span>
                  </>
                )}
              </NavLink>
            )
          })}
        </div>
      </div>

      {/* Local Animations für FAB-Pulse + Gradient-Shift */}
      <style>{`
        @keyframes fab-pulse {
          0%, 100% { transform: scale(1); opacity: 0.50; }
          50%      { transform: scale(1.10); opacity: 0.75; }
        }
        @keyframes fab-pulse-active {
          0%, 100% { transform: scale(1); opacity: 0.65; }
          50%      { transform: scale(1.18); opacity: 0.95; }
        }
        @keyframes fab-gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
        .animate-fab-pulse        { animation: fab-pulse        2.6s ease-in-out infinite; }
        .animate-fab-pulse-active { animation: fab-pulse-active 1.8s ease-in-out infinite; }
      `}</style>
    </nav>
  )
}

function SafeBottomSpacer() {
  return <div className="md:hidden h-2" aria-hidden="true" />
}

// ── Footer (nur Desktop) ────────────────────────────────────────────────────
function Footer() {
  const t = useT()
  return (
    <footer className="px-6 py-8 border-t mt-12" style={{ borderColor: 'var(--border)' }}>
      <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs text-app-text-mute">
        <div>CustoSoft · © 2026 · {t('footer.madeIn')}</div>
        <div className="flex flex-wrap gap-4">
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/impressum">{t('footer.impressum')}</a>
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/datenschutz">{t('footer.datenschutz')}</a>
          <a className="hover:text-app-text transition" href="https://custosoft-api.davidschroedinger.workers.dev/agb">{t('footer.agb')}</a>
          <a className="hover:text-app-text transition" href="mailto:taironic.media@gmail.com">{t('footer.contact')}</a>
        </div>
      </div>
    </footer>
  )
}

// ── Avatar-Helper ───────────────────────────────────────────────────────────
function Avatar({ url, initials, size }: { url: string | null; initials: string; size: number }) {
  if (url) {
    const src = url.startsWith('http') ? url : `https://custosoft-api.davidschroedinger.workers.dev${url}`
    return (
      <img
        src={src}
        alt=""
        width={size} height={size}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    )
  }
  return (
    <div
      className="rounded-full grid place-items-center font-bold text-white bg-gradient-to-br from-accent to-accent2"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials || '?'}
    </div>
  )
}

// ── Theme-Segmented Control ─────────────────────────────────────────────────
export function ThemeSegmented({
  value,
  onChange,
}: {
  value: ThemeChoice
  onChange: (t: ThemeChoice) => void
}) {
  const opts: { v: ThemeChoice; l: string; icon: string }[] = [
    { v: 'light',  l: 'Hell',    icon: '☀️' },
    { v: 'system', l: 'System',  icon: '🖥️' },
    { v: 'dark',   l: 'Dunkel',  icon: '🌙' },
  ]
  return (
    <div
      className="grid grid-cols-3 gap-1 p-1 rounded-xl"
      style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}
    >
      {opts.map(o => {
        const active = value === o.v
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`flex flex-col items-center gap-0.5 py-2 rounded-lg transition text-[11px] font-medium ${
              active
                ? 'bg-accent text-white shadow-md shadow-accent/30'
                : 'text-app-text-dim hover:bg-app-elev hover:text-app-text'
            }`}
          >
            <span className="text-base">{o.icon}</span>
            <span>{o.l}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Theme-Hook ──────────────────────────────────────────────────────────────
export function useThemeChoice(): ThemeChoice {
  const [t, setT] = useState<ThemeChoice>(getThemeChoice())
  useEffect(() => {
    setT(getThemeChoice())
    const unsub = subscribeTheme(setT)
    return () => { unsub() }
  }, [])
  return t
}

export function useResolvedTheme(): 'light' | 'dark' {
  const choice = useThemeChoice()
  const [resolved, setResolved] = useState<'light' | 'dark'>(getResolvedTheme())
  useEffect(() => {
    setResolved(getResolvedTheme())
    if (choice === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: light)')
      const fn = () => setResolved(getResolvedTheme())
      mq.addEventListener('change', fn)
      return () => mq.removeEventListener('change', fn)
    }
  }, [choice])
  return resolved
}
